import {
  AutoPopulateConfig,
  CustomCollectionsFieldConfig,
  Entry,
} from "@dreambigger/shared/src/types";
import compileTemplate from "../../../utils/compileTemplate";
import { ADDRESS_SUBKEY_SUFFIXES } from "./addressSubkeys";

/** Matches a pure Handlebars step reference like `{{{applicant-address__address}}}`
 *  (double-brace form tolerated). Both the slug and field-key capture groups
 *  accept hyphens so cross-step references like `{{{step-one__my-field}}}` are
 *  recognized. Templates with any surrounding text are treated as composites
 *  and skipped for address-subkey inference. */
const PURE_REF_RE = /^\{\{\{?([\w-]+)__([\w-]+)\}?\}\}$/;

/**
 * Resolve an autoPopulate config's fieldMapping into an Entry partial.
 * String values are Handlebars templates rendered against the flattened
 * prefixed-responses map (`{{{slug__fieldKey}}}`); primitives pass through.
 * For `fieldType: "address"` destinations mapped via a pure step reference,
 * the five hidden subkeys are also seeded from the source — looked up first
 * nested (custom-collections address field sources) then top-level (address-step
 * sources) — so the AddressAutocomplete validator passes on reload without
 * forcing the user to re-select.
 */
export default function loadAutoPopulateData(
  config: AutoPopulateConfig,
  flattened: Record<string, unknown>,
  fieldConfigs: CustomCollectionsFieldConfig[]
): Partial<Entry> {
  const data: Partial<Entry> = {};
  for (const [entryField, mapping] of Object.entries(config.fieldMapping)) {
    if (typeof mapping === "string") {
      const rendered = compileTemplate(mapping, flattened);
      if (rendered !== "") data[entryField] = rendered;
    } else {
      data[entryField] = mapping;
    }
  }

  for (const fc of fieldConfigs) {
    if (fc.fieldType !== "address") continue;
    const mapping = config.fieldMapping[fc.fieldKey];
    if (typeof mapping !== "string") continue;
    const match = PURE_REF_RE.exec(mapping.trim());
    if (!match) continue;
    const [, sourceSlug, sourceBase] = match;
    for (const suffix of ADDRESS_SUBKEY_SUFFIXES) {
      const nested = flattened[`${sourceSlug}__${sourceBase}__${suffix}`];
      const topLevel = flattened[`${sourceSlug}__${suffix}`];
      const value = nested !== undefined ? nested : topLevel;
      if (value !== undefined && value !== "") {
        data[`${fc.fieldKey}__${suffix}`] = value as Entry[string];
      }
    }
  }
  return data;
}
